// The operator-level conversation (Brief 4 / P8) — a message list + composer,
// shared by the Global room, a PROJECT's room, and a session's Operators view
// ("every level is a chat"). Presentational and pure: it renders the messages
// it's given and reports a send; the data + send come from a channel hook.
// Token-pure (var(--waytide-sys-*)).
//
// Heart: it reads as a real conversation, not a log. Your own sends align to the
// trailing edge with a calm tint; the Operators — Supervisor (the guard), the
// three domain arbiters (Engineering / Design / Product), Signal (the
// signal) — carry a name and a glyph, their name in
// primary text on a calm neutral left rule. They do NOT carry the reserved
// attention accent: the amber signal-attention is reserved exclusively for
// needs-me, so an operator speaking never mints a second attention signal (each
// operator's own dedicated watch-* accent only tints its own controls, e.g. a
// steering card edge). The agent is neutral. New lines settle in with the shared
// `wt-rise` (reduced-motion safe). The composer is a real one: Enter sends,
// Shift+Enter newlines, and it grows with your message.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ArrowUp,
  Bot,
  Compass,
  MessageCircle,
  Mic,
  PenTool,
  Radio,
  Shield,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { appendDictation, type ChatMessage } from "@waytide/core";
import { webDictation } from "../speech/webSpeech";
import { OPERATORS } from "@waytide/ui";
import { usePersistentDraft } from "@waytide/ui";
import type { OutboxEntry } from "@waytide/ui";
import {
  parseDecision,
  parseResolved,
  resolvedDecisionIds,
} from "./steering";
import {
  SteeringDecisionCard,
  SteeringMark,
} from "./SteeringDecisionCard";

/** The Operators that post catches into a thread, and their glyph: Supervisor =
 * the guard (shield); the three domain arbiters — Engineering (wrench),
 * Design (pen tool), Product (compass); Signal = the signal (radio). Keyed by
 * the stable internal ids supervisor/engineering/design/product/signal (the
 * wire/sender_kind values); the displayed name is the current canon from
 * OPERATORS. */
const OPERATOR_KINDS = new Set([
  "supervisor",
  "engineering",
  "design",
  "product",
  "signal",
]);
const OPERATOR_ICON: Record<string, LucideIcon> = {
  supervisor: Shield,
  engineering: Wrench,
  design: PenTool,
  product: Compass,
  signal: Radio,
};

function isOperatorMessage(m: ChatMessage): boolean {
  return OPERATOR_KINDS.has(m.sender_kind);
}

/** Your own sends (the orchestrator) — aligned to the trailing edge, the
 * universal "this is me" of a chat. */
function isMine(m: ChatMessage): boolean {
  return m.sender_kind === "orchestrator";
}

/** The author label: your own sends show your email; an Operator shows its
 * canon display name (Supervisor / Engineering / Design / Product / Signal)
 * from OPERATORS, looked up by the stable internal id its sender_kind
 * carries; anything else (the agent) shows its kind. */
function senderLabel(m: ChatMessage): string {
  if (isMine(m)) return m.sender_email;
  if (isOperatorMessage(m)) {
    return OPERATORS[m.sender_kind]?.name ?? m.sender_kind;
  }
  return m.sender_kind;
}

/** The little identity mark before the author name: you = a filled calm disc;
 * an Operator = its glyph in the attention hue; the agent = a neutral bot. */
function SenderMark({ m }: { m: ChatMessage }) {
  if (isMine(m)) {
    return (
      <span
        className="wt-dash-msg__mark wt-dash-msg__mark--me"
        aria-hidden="true"
      />
    );
  }
  const Icon = OPERATOR_ICON[m.sender_kind] ?? Bot;
  return <Icon className="wt-dash-msg__mark" size={13} aria-hidden="true" />;
}

export function Conversation({
  messages,
  onSend,
  placeholder,
  emptyHint,
  draftScope,
  pending = [],
  onRetry,
}: {
  messages: ChatMessage[];
  onSend: (body: string) => void;
  placeholder: string;
  emptyHint: string;
  /** A stable per-composer id (e.g. the thread id, or "global"). Persists the
   * draft across reloads/crashes, keyed per composer (Brief 5 / point 15).
   * Omit it and the draft is in-memory only (back-compat). */
  draftScope?: string | number | null;
  /** Pending (unsent / retrying) sends for this composer, rendered after the
   * confirmed messages so the user's words are visible the moment they Send. */
  pending?: OutboxEntry[];
  /** Re-arm a parked (permanently-failed) pending send — wires the Retry on a
   * failed row. Omit it and a failed row shows the error without a retry button. */
  onRetry?: (key: string) => void;
}) {
  // The draft is mirrored to storage on every keystroke and restored on mount,
  // so a reload mid-typing keeps the text exactly where it was (point 15).
  const { draft, setDraft, clearDraft } = usePersistentDraft(draftScope ?? null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the composer to its content (then it scrolls) — no library.
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  // Voice in (Brief 4 / P4b, web slice): dictate into the draft via the shared
  // browser-speech seam. No engine on this platform ⇒ no mic (graceful
  // absence); finalized fragments append through the pure `appendDictation`.
  const [listening, setListening] = useState(false);
  const stopDictation = useRef<(() => void) | null>(null);
  const micAvailable = webDictation.available();
  useEffect(() => () => stopDictation.current?.(), []);

  function toggleDictation() {
    if (listening) {
      stopDictation.current?.();
      stopDictation.current = null;
      setListening(false);
      return;
    }
    setListening(true);
    stopDictation.current = webDictation.start(
      (fragment) => setDraft((d) => appendDictation(d, fragment)),
      () => {
        stopDictation.current = null;
        setListening(false);
      },
    );
  }

  function submit() {
    const body = draft.trim();
    if (!body) return;
    // Hand the message to the durable send path FIRST (the outbox persists it),
    // then clear the composer — so clearing can never lose it (point 15).
    onSend(body);
    clearDraft();
  }

  // Brief 5 pt17: which steering decisions are already resolved (a resolved
  // marker exists in the thread) — so a decision card renders collapsed after a
  // reload OR the instant another surface answers (the marker arrives live).
  const resolved = resolvedDecisionIds(messages);

  return (
    <div className="wt-dash-convo">
      {messages.length === 0 && pending.length === 0 ? (
        <div className="wt-dash-convo__empty">
          <MessageCircle
            size={22}
            aria-hidden="true"
            className="wt-dash-convo__empty-mark"
          />
          <p className="wt-dash-empty">{emptyHint}</p>
        </div>
      ) : (
        <ul className="wt-dash-msgs" aria-live="polite">
          {messages.map((m) => {
            // pt17: a steering-decision aside renders as a pick-one card (with
            // Signal's head), not a raw JSON body. A resolved-marker aside is
            // product plumbing — it drives the card's collapsed state and
            // renders nothing of its own.
            const decision = parseDecision(m);
            if (decision) {
              return (
                <li
                  key={m.id}
                  className="wt-dash-msg wt-dash-msg--operator wt-rise"
                  data-sender-kind={m.sender_kind}
                  data-decision={decision.decision_id}
                >
                  <span className="wt-dash-msg__head">
                    <SteeringMark />
                    <span className="wt-dash-msg__who">
                      {OPERATORS.signal?.name ?? "Signal"}
                    </span>
                  </span>
                  <SteeringDecisionCard
                    decision={decision}
                    resolved={resolved.has(decision.decision_id)}
                  />
                </li>
              );
            }
            if (parseResolved(m)) return null;

            return (
              <li
                key={m.id}
                className={`wt-dash-msg wt-rise${
                  isMine(m) ? " wt-dash-msg--mine" : ""
                }${isOperatorMessage(m) ? " wt-dash-msg--operator" : ""}`}
                data-sender-kind={m.sender_kind}
                data-operator={isOperatorMessage(m) ? m.sender_kind : undefined}
              >
                <span className="wt-dash-msg__head">
                  <SenderMark m={m} />
                  <span className="wt-dash-msg__who">{senderLabel(m)}</span>
                </span>
                <span className="wt-dash-msg__body">{m.body}</span>
              </li>
            );
          })}
          {/* Pending sends (Brief 5 / point 15): your own words, persisted and
              retrying on reconnect — aligned like your sends, marked pending. */}
          {pending.map((p) => (
            <li
              key={p.key}
              className={`wt-dash-msg wt-dash-msg--mine wt-dash-msg--pending wt-rise${
                p.failed ? " wt-dash-msg--failed" : ""
              }`}
              data-testid="pending-message"
              data-pending="true"
              data-failed={p.failed ? "true" : undefined}
            >
              <span className="wt-dash-msg__head">
                <span
                  className="wt-dash-msg__mark wt-dash-msg__mark--me"
                  aria-hidden="true"
                />
                <span className="wt-dash-msg__who">
                  {p.failed
                    ? `Couldn't send${p.lastError ? `: ${p.lastError}` : ""}`
                    : p.attempts > 0
                      ? "Reconnecting… will send"
                      : "Sending…"}
                </span>
              </span>
              <span className="wt-dash-msg__body">{p.body}</span>
              {p.failed && onRetry ? (
                <button
                  type="button"
                  className="wt-dash-retry"
                  data-testid="pending-retry"
                  onClick={() => onRetry(p.key)}
                >
                  Retry
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <form
        className="wt-dash-composer"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="wt-dash-composer__field">
          <textarea
            ref={taRef}
            className="wt-dash-input"
            value={draft}
            rows={1}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter is a newline (a real composer).
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={listening ? "Listening…" : placeholder}
            aria-label={placeholder}
          />
          {micAvailable ? (
            <button
              type="button"
              className="wt-dash-mic"
              data-listening={listening ? "true" : "false"}
              aria-label={listening ? "Stop dictation" : "Dictate"}
              aria-pressed={listening}
              data-testid="composer-mic"
              onClick={toggleDictation}
            >
              <Mic size={17} aria-hidden="true" />
            </button>
          ) : null}
          <button
            type="submit"
            className="wt-dash-send"
            disabled={!draft.trim()}
            aria-label="Send"
          >
            <ArrowUp size={18} aria-hidden="true" />
          </button>
        </div>
      </form>
    </div>
  );
}
